import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

const flattenFolderTree = (tree, result = []) => {
  if (!Array.isArray(tree)) return result;
  
  for (const folder of tree) {
    result.push({
      id: folder.id,
      name: folder.name,
      parent_folder_id: folder.parent_folder_id,
      path: folder.path,
      document_count: folder.document_count || 0
    });
    if (folder.subfolders && folder.subfolders.length > 0) {
      flattenFolderTree(folder.subfolders, result);
    }
  }
  return result;
};

export const fetchFolders = createAsyncThunk(
  'folders/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/folders/');
      const tree = response.data;
      return flattenFolderTree(tree);
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch folders');
    }
  }
);

export const createFolder = createAsyncThunk(
  'folders/create',
  async ({ name, parentFolderId }, { rejectWithValue }) => {
    try {
      const response = await api.post('/folders/', {
        name,
        parent_folder_id: parentFolderId || null
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create folder');
    }
  }
);

export const deleteFolder = createAsyncThunk(
  'folders/delete',
  async (folderId, { rejectWithValue }) => {
    console.log('🗑️ [Redux] deleteFolder thunk called with folderId:', folderId);
    try {
      console.log('🗑️ [Redux] Making DELETE request to:', `/folders/${folderId}/`);
      const response = await api.delete(`/folders/${folderId}/`);
      console.log('🗑️ [Redux] DELETE response:', response);
      return folderId;
    } catch (error) {
      console.error('🗑️ [Redux] DELETE error:', error);
      console.error('🗑️ [Redux] Error response:', error.response);
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete folder');
    }
  }
);

export const moveDocument = createAsyncThunk(
  'folders/moveDocument',
  async ({ documentId, folderId }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/documents/${documentId}`, {
        folder_id: folderId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to move document');
    }
  }
);

const foldersSlice = createSlice({
  name: 'folders',
  initialState: {
    items: [],
    selectedFolderId: null,
    loading: false,
    error: null
  },
  reducers: {
    setSelectedFolder: (state, action) => {
      state.selectedFolderId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFolders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload || [];
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createFolder.pending, (state) => {
        state.error = null;
      })
      .addCase(createFolder.fulfilled, (state, action) => {
        const newFolder = {
          id: action.payload.id,
          name: action.payload.name,
          parent_folder_id: action.payload.parent_folder_id,
          path: action.payload.path,
          document_count: 0
        };
        state.items.push(newFolder);
        state.error = null;
      })
      .addCase(createFolder.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteFolder.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.items = state.items.filter(f => f.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(moveDocument.fulfilled, (state) => {
      });
  }
});

export const { setSelectedFolder, clearError } = foldersSlice.actions;
export default foldersSlice.reducer;
